export const interpolateTemplate = (str: string) => {
  return function interpolate(o: Record<string, string>) {
    return str.replace(/{{([^{}]*)}}/g, function replacer(a, b) {
      const r = o[b];
      return typeof r === "string" || typeof r === "number" ? r : a;
    });
  };
};

import {
  Milestone,
  ProgressBarType,
  PROGRESS_BAR_TYPE,
  MilestoneCategory,
  MilestoneBasis,
} from "./types";

export interface ProgressBarData {
  enable: boolean;
  category: MilestoneCategory;
  milestones: Milestone[];
  complete_message: string;
  complete_message_enable: boolean;
  text_title_color: string;
  progress_bar_type: PROGRESS_BAR_TYPE;
  milestone_price_type?: MilestoneBasis;
  text_amount_color: string;
  text_header_color: string;
  text_title_enable: boolean;
  text_amount_enable: boolean;
  text_header_enable: boolean;
  milestone_filled_color: string;
  background_color: string;
}

export const DEFAULT_PROGRESS_BAR = {
  isEnabled: false,
  milestones: [],
  customizations: {
    type: PROGRESS_BAR_TYPE.STATIC,
    category: MilestoneCategory.PRICE_BASED,
    completedMessage: "You have successfully reached the milestone",
    completedMessage_enabled: true,
    textonProgressBar_header: true,
    textonProgressBar_headerColor: "#2c874a",
    textonProgressBar_amount: true,
    textonProgressBar_amountColor: "#2c874a",
    textonProgressBar_title: true,
    textonProgressBar_titleColor: "#2c874a",
    filledColor: "#2c874a",
    backgroundColor: "#2c874a",
  },
};

export const DEFAULT_PROGRESS_BAR_COUNT_BASED = {
  ...DEFAULT_PROGRESS_BAR,
  customizations: {
    ...DEFAULT_PROGRESS_BAR.customizations,
    category: MilestoneCategory.COUNT_BASED,
  },
};

export const DEFAULT_PROGRESS_BAR_AMOUNT_BASED = {
  ...DEFAULT_PROGRESS_BAR,
  customizations: {
    ...DEFAULT_PROGRESS_BAR.customizations,
    category: MilestoneCategory.PRICE_BASED,
  },
};

export const parseCartProgressBar = (
  response: ProgressBarData,
): ProgressBarType => {
  const {
    enable,
    category,
    milestones,
    complete_message,
    text_title_color,
    progress_bar_type,
    milestone_price_type,
    text_amount_color,
    text_header_color,
    text_title_enable,
    text_amount_enable,
    text_header_enable,
    milestone_filled_color,
    complete_message_enable,
    background_color,
  } = response;

  const milestonesWithIndex = milestones.map((milestone, index) => ({
    ...milestone,
    milestone: index,
  }));

  return {
    isEnabled: enable,
    milestones: milestonesWithIndex,
    customizations: {
      type: progress_bar_type,
      category,
      completedMessage: complete_message,
      completedMessage_enabled: complete_message_enable,
      textonProgressBar_header: text_header_enable,
      textonProgressBar_headerColor: text_header_color,
      textonProgressBar_amount: text_amount_enable,
      textonProgressBar_amountColor: text_amount_color,
      textonProgressBar_title: text_title_enable,
      textonProgressBar_titleColor: text_title_color,
      filledColor: milestone_filled_color,
      backgroundColor: background_color,
      milestoneBasis: milestone_price_type,
    },
  };
};

export const parseCartResponse = (
  response: ProgressBarData[],
): { countBased: ProgressBarType; amountBased: ProgressBarType } => {
  const progressBarData = response;
  const countbasedProgressBarData = progressBarData.filter(
    (progressbar) => progressbar.category === MilestoneCategory.COUNT_BASED,
  );
  const amountbasedProgressBarData = progressBarData.filter(
    (progressbar) => progressbar.category === MilestoneCategory.PRICE_BASED,
  );

  const returnObject: {
    countBased: ProgressBarType;
    amountBased: ProgressBarType;
  } = {
    countBased: DEFAULT_PROGRESS_BAR_COUNT_BASED,
    amountBased: DEFAULT_PROGRESS_BAR_AMOUNT_BASED,
  };

  if (countbasedProgressBarData.length > 0 && countbasedProgressBarData[0]) {
    returnObject.countBased = parseCartProgressBar(
      countbasedProgressBarData[0],
    );
  }

  if (amountbasedProgressBarData.length > 0 && amountbasedProgressBarData[0]) {
    returnObject.amountBased = parseCartProgressBar(
      amountbasedProgressBarData[0],
    );
  }

  return returnObject;
};

export const parseProgressBarForBackend = (
  progressBar: ProgressBarType,
): ProgressBarData => {
  const { isEnabled, milestones, customizations } = progressBar;

  return {
    enable: isEnabled,
    category: customizations.category,
    // TODO: add this back when we enable pricebased
    // category: MilestoneCategory.PRICE_BASED,
    milestones,
    complete_message: customizations.completedMessage,
    complete_message_enable: customizations.completedMessage_enabled,
    progress_bar_type: customizations.type,
    text_title_color: customizations.textonProgressBar_titleColor,
    text_amount_color: customizations.textonProgressBar_amountColor,
    text_header_color: customizations.textonProgressBar_headerColor,
    text_title_enable: customizations.textonProgressBar_title,
    text_amount_enable: customizations.textonProgressBar_amount,
    text_header_enable: customizations.textonProgressBar_header,
    milestone_filled_color: customizations.filledColor,
    background_color: customizations.backgroundColor,
    milestone_price_type: customizations.milestoneBasis,
  };
};

/**
 * Determines if a color is in the light or dark band.
 * @param color - The color to check, in hex format (e.g., "#RRGGBB" or "#RGB")
 * @param threshold - The brightness threshold (0-255). Default is 128.
 * @returns true if the color is light, false if it's dark
 */
export function isLightColor(color: string, threshold: number = 90): boolean {
  // Remove the hash if it exists
  color = color.replace(/^#/, "");

  // Convert short hex to full hex
  if (color.length === 3) {
    color = color
      .split("")
      .map((char) => char + char)
      .join("");
  }

  // Convert hex to RGB
  const r = parseInt(color.slice(0, 2), 16);
  const g = parseInt(color.slice(2, 4), 16);
  const b = parseInt(color.slice(4, 6), 16);

  // Calculate perceived brightness using the sRGB color space formula
  // Brightness = (0.299*R + 0.587*G + 0.114*B)
  const brightness = 0.299 * r + 0.587 * g + 0.114 * b;

  // Return true if the color is light, false if it's dark
  return brightness > threshold;
}
